var __defProp = Object.defineProperty;
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _menuDrawerHiddenWidth, _intersectionObserver, _scrollContainer, _offscreen, _lastScrollTop, _timeout, _scrollRafId, _resizeObserver, _observeStickyPosition, _handleOverflowMinimum, _HeaderComponent_instances, updateMenuVisibility_fn, _handleBreakpointChange, _handleWindowScroll, _updateScrollState;
import { Component } from "@theme/component";
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from "@theme/utilities";
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery
} from "@theme/scroll-container";
class HeaderComponent extends Component {
  constructor() {
    super(...arguments);
    __privateAdd(this, _HeaderComponent_instances);
    __publicField(this, "requiredRefs", ["headerDrawerContainer", "headerMenu", "headerRowTop"]);
    /**
     * Width of window when header drawer was hidden
     * @type {number | null}
     */
    __privateAdd(this, _menuDrawerHiddenWidth, null);
    /**
     * An intersection observer for monitoring sticky header position
     * @type {IntersectionObserver | null}
     */
    __privateAdd(this, _intersectionObserver, null);
    /** @type {EventTarget | null} */
    __privateAdd(this, _scrollContainer, null);
    /**
     * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
     * @type {boolean}
     */
    __privateAdd(this, _offscreen, false);
    /**
     * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
     * @type {number}
     */
    __privateAdd(this, _lastScrollTop, 0);
    /**
     * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
     * @type {number | null}
     */
    __privateAdd(this, _timeout, null);
    /**
     * RAF ID for scroll handler throttling
     * @type {number | null}
     */
    __privateAdd(this, _scrollRafId, null);
    /**
     * Keeps the global `--header-height` custom property up to date,
     * which other theme components can then consume
     */
    __privateAdd(this, _resizeObserver, new ResizeObserver(([entry]) => {
      if (!entry || !entry.borderBoxSize[0]) return;
      const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
      document.body.style.setProperty("--header-height", "".concat(roundedHeaderHeight, "px"));
      if (__privateGet(this, _menuDrawerHiddenWidth) && window.innerWidth > __privateGet(this, _menuDrawerHiddenWidth)) {
        __privateMethod(this, _HeaderComponent_instances, updateMenuVisibility_fn).call(this, false);
      }
    }));
    /**
     * Observes the header while scrolling the viewport to track when its actively sticky
     * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
     */
    __privateAdd(this, _observeStickyPosition, (alwaysSticky = true) => {
      if (__privateGet(this, _intersectionObserver)) return;
      const config = {
        threshold: alwaysSticky ? 1 : 0,
        root: getIntersectionRoot()
      };
      __privateSet(this, _intersectionObserver, new IntersectionObserver(([entry]) => {
        if (!entry) return;
        const { isIntersecting } = entry;
        if (alwaysSticky) {
          this.dataset.stickyState = isIntersecting ? "inactive" : "active";
          if (this.dataset.themeColor && !document.documentElement.hasAttribute("data-pb-announcement-color")) {
            changeMetaThemeColor(this.dataset.themeColor);
          }
        } else {
          __privateSet(this, _offscreen, !isIntersecting || this.dataset.stickyState === "active");
        }
      }, config));
      __privateGet(this, _intersectionObserver).observe(this);
    });
    /**
     * Handles the overflow minimum event from the header menu
     * @param {OverflowMinimumEvent} event
     */
    __privateAdd(this, _handleOverflowMinimum, (event) => {
      __privateMethod(this, _HeaderComponent_instances, updateMenuVisibility_fn).call(this, event.detail.minimumReached);
    });
    /**
     * Rebinds the scroll listener and IntersectionObserver when the viewport
     * crosses the squeeze breakpoint (990px). The scroll container switches
     * between `.page-wrapper` (desktop) and `document.scrollingElement` (mobile),
     * so cached bindings from initialization become stale after a resize.
     */
    __privateAdd(this, _handleBreakpointChange, () => {
      var _a;
      const stickyMode = this.getAttribute("sticky");
      if (!stickyMode) return;
      if (__privateGet(this, _scrollContainer)) {
        __privateGet(this, _scrollContainer).removeEventListener("scroll", __privateGet(this, _handleWindowScroll));
        __privateSet(this, _scrollContainer, getScrollEventTarget());
        __privateGet(this, _scrollContainer).addEventListener("scroll", __privateGet(this, _handleWindowScroll));
      }
      (_a = __privateGet(this, _intersectionObserver)) == null ? void 0 : _a.disconnect();
      __privateSet(this, _intersectionObserver, null);
      __privateGet(this, _observeStickyPosition).call(this, stickyMode === "always");
    });
    __privateAdd(this, _handleWindowScroll, () => {
      if (__privateGet(this, _scrollRafId) !== null) return;
      __privateSet(this, _scrollRafId, requestAnimationFrame(() => {
        __privateSet(this, _scrollRafId, null);
        __privateGet(this, _updateScrollState).call(this);
      }));
    });
    __privateAdd(this, _updateScrollState, () => {
      const stickyMode = this.getAttribute("sticky");
      if (!__privateGet(this, _offscreen) && stickyMode !== "always") return;
      const scrollTop = getScrollTop();
      const headerTop = this.getBoundingClientRect().top;
      const isScrollingUp = scrollTop < __privateGet(this, _lastScrollTop);
      const isAtTop = headerTop >= 0;
      if (__privateGet(this, _timeout)) {
        clearTimeout(__privateGet(this, _timeout));
        __privateSet(this, _timeout, null);
      }
      if (stickyMode === "always") {
        if (isAtTop) {
          this.dataset.scrollDirection = "none";
        } else if (isScrollingUp) {
          this.dataset.scrollDirection = "up";
        } else {
          this.dataset.scrollDirection = "down";
        }
        __privateSet(this, _lastScrollTop, scrollTop);
        return;
      }
      if (isScrollingUp) {
        if (isAtTop) {
          __privateSet(this, _offscreen, false);
          this.dataset.stickyState = "inactive";
          this.dataset.scrollDirection = "none";
        } else {
          this.dataset.stickyState = "active";
          this.dataset.scrollDirection = "up";
        }
      } else if (this.dataset.stickyState === "active") {
        this.dataset.scrollDirection = "none";
        this.dataset.stickyState = "idle";
      } else {
        this.dataset.scrollDirection = "none";
        this.dataset.stickyState = "idle";
      }
      __privateSet(this, _lastScrollTop, scrollTop);
    });
  }
  connectedCallback() {
    super.connectedCallback();
    __privateGet(this, _resizeObserver).observe(this);
    this.addEventListener("overflowMinimum", __privateGet(this, _handleOverflowMinimum));
    const stickyMode = this.getAttribute("sticky");
    if (stickyMode) {
      __privateGet(this, _observeStickyPosition).call(this, stickyMode === "always");
      if (stickyMode === "scroll-up" || stickyMode === "always") {
        __privateSet(this, _scrollContainer, getScrollEventTarget());
        __privateGet(this, _scrollContainer).addEventListener("scroll", __privateGet(this, _handleWindowScroll));
      }
      scrollContainerMediaQuery.addEventListener("change", __privateGet(this, _handleBreakpointChange));
    }
  }
  disconnectedCallback() {
    var _a, _b;
    super.disconnectedCallback();
    __privateGet(this, _resizeObserver).disconnect();
    (_a = __privateGet(this, _intersectionObserver)) == null ? void 0 : _a.disconnect();
    this.removeEventListener("overflowMinimum", __privateGet(this, _handleOverflowMinimum));
    scrollContainerMediaQuery.removeEventListener("change", __privateGet(this, _handleBreakpointChange));
    (_b = __privateGet(this, _scrollContainer)) == null ? void 0 : _b.removeEventListener("scroll", __privateGet(this, _handleWindowScroll));
    __privateSet(this, _scrollContainer, null);
    if (__privateGet(this, _scrollRafId) !== null) {
      cancelAnimationFrame(__privateGet(this, _scrollRafId));
      __privateSet(this, _scrollRafId, null);
    }
    document.body.style.setProperty("--header-height", "0px");
  }
}
_menuDrawerHiddenWidth = new WeakMap();
_intersectionObserver = new WeakMap();
_scrollContainer = new WeakMap();
_offscreen = new WeakMap();
_lastScrollTop = new WeakMap();
_timeout = new WeakMap();
_scrollRafId = new WeakMap();
_resizeObserver = new WeakMap();
_observeStickyPosition = new WeakMap();
_handleOverflowMinimum = new WeakMap();
_HeaderComponent_instances = new WeakSet();
/**
 * Updates the visibility of the menu and drawer
 * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
 */
updateMenuVisibility_fn = function(hideMenu) {
  if (hideMenu) {
    __privateSet(this, _menuDrawerHiddenWidth, window.innerWidth);
  } else {
    __privateSet(this, _menuDrawerHiddenWidth, null);
    const overflowList = this.querySelector("overflow-list");
    if (overflowList) overflowList.removeAttribute("minimum-reached");
  }
  setHeaderMenuStyle();
};
_handleBreakpointChange = new WeakMap();
_handleWindowScroll = new WeakMap();
_updateScrollState = new WeakMap();
if (!customElements.get("header-component")) {
  customElements.define("header-component", HeaderComponent);
}
onDocumentLoaded(() => {
  const header = document.querySelector("header-component");
  const headerGroup = document.querySelector("#header-group");
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        var _a, _b, _c;
        if (entry.target !== header || header.hasAttribute("transparent") && ((_a = header.parentElement) == null ? void 0 : _a.nextElementSibling)) {
          return totalHeight + ((_c = (_b = entry.borderBoxSize[0]) == null ? void 0 : _b.blockSize) != null ? _c : 0);
        }
        return totalHeight;
      }, 0);
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty("--header-group-height", "".concat(roundedHeaderGroupHeight, "px"));
    });
    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "childList") {
          const children2 = headerGroup.children;
          for (let i = 0; i < children2.length; i++) {
            const element = children2[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });
    mutationObserver.observe(headerGroup, { childList: true });
  }
});
